import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, Users, CalendarCheck2, Stethoscope, Package,
  AlertTriangle, ChevronDown, Clock, UserCheck, ClipboardList,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '@/lib/axios';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getEstadoCitaInfo } from '@/types/cita';

/* ── Date range ─────────────────────────────────────────────────────────── */

type Preset = 'mes_actual' | 'mes_anterior' | 'ultimos_30' | 'ultimos_90' | 'anio_actual' | 'personalizado';

interface Rango { fechaInicio: string; fechaFin: string }

function presetToRango(preset: Preset, custom: Rango): Rango {
  const hoy = new Date();
  switch (preset) {
    case 'mes_actual':
      return { fechaInicio: format(startOfMonth(hoy), 'yyyy-MM-dd'), fechaFin: format(endOfMonth(hoy), 'yyyy-MM-dd') };
    case 'mes_anterior': {
      const ant = subMonths(hoy, 1);
      return { fechaInicio: format(startOfMonth(ant), 'yyyy-MM-dd'), fechaFin: format(endOfMonth(ant), 'yyyy-MM-dd') };
    }
    case 'ultimos_30':
      return { fechaInicio: format(subDays(hoy, 30), 'yyyy-MM-dd'), fechaFin: format(hoy, 'yyyy-MM-dd') };
    case 'ultimos_90':
      return { fechaInicio: format(subDays(hoy, 90), 'yyyy-MM-dd'), fechaFin: format(hoy, 'yyyy-MM-dd') };
    case 'anio_actual':
      return { fechaInicio: format(startOfYear(hoy), 'yyyy-MM-dd'), fechaFin: format(hoy, 'yyyy-MM-dd') };
    default:
      return custom;
  }
}

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'mes_actual',    label: 'Este mes' },
  { value: 'mes_anterior',  label: 'Mes anterior' },
  { value: 'ultimos_30',    label: 'Últimos 30 días' },
  { value: 'ultimos_90',    label: 'Últimos 90 días' },
  { value: 'anio_actual',   label: 'Este año' },
  { value: 'personalizado', label: 'Personalizado' },
];

/* ── Constants ──────────────────────────────────────────────────────────── */

const CHART_COLORS = {
  primary: '#7c3aed', green: '#16a34a', blue: '#2563eb',
  orange: '#ea580c',  red: '#dc2626',   gray: '#6b7280',
};

const METODO_COLOR: Record<string, string> = {
  efectivo: CHART_COLORS.green, tarjeta: CHART_COLORS.blue,
  transferencia: CHART_COLORS.primary, stripe: '#6366f1',
};
const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', stripe: 'Stripe',
};
/* ── Shared helpers ─────────────────────────────────────────────────────── */

function fmt(d: string) { return format(new Date(d + 'T12:00:00'), 'd MMM', { locale: es }); }

const tooltipStyle = {
  backgroundColor: '#fff', border: '1px solid #e5e7eb',
  borderRadius: 12, fontSize: 12, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
};

function KpiCard({ icon: Icon, label, value, sub, color = 'primary' }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string;
  color?: 'primary' | 'green' | 'blue' | 'orange' | 'red';
}) {
  const bg  = { primary: 'bg-primary-50', green: 'bg-green-50',  blue: 'bg-blue-50', orange: 'bg-orange-50', red: 'bg-red-50' };
  const ic  = { primary: 'text-primary-600', green: 'text-green-600', blue: 'text-blue-600', orange: 'text-orange-600', red: 'text-red-600' };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
      <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0', bg[color])}>
        <Icon size={20} className={ic[color]} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-text-primary truncate">{value}</p>
        <p className="text-xs text-text-secondary mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-text-secondary mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h3 className="text-base font-bold text-text-primary mb-5">{title}</h3>
      {children}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <div className="flex items-center justify-center h-40 text-sm text-text-secondary">{label}</div>;
}

/* ── Admin sections ─────────────────────────────────────────────────────── */

function SeccionIngresos({ rango }: { rango: Rango }) {
  const { data, isLoading } = useQuery({
    queryKey: ['reporte-ingresos', rango.fechaInicio, rango.fechaFin],
    queryFn: () => api.get('/api/reportes/ingresos', { params: rango }).then(r => r.data as {
      totalPeriodo: number;
      porMetodo: Record<string, number>;
      porDia: { fecha: string; total: number }[];
    }),
  });
  const metodos = data ? Object.entries(data.porMetodo).map(([metodo, total]) => ({ metodo, total })) : [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={TrendingUp} label="Total del período" value={formatCurrency(data?.totalPeriodo ?? 0)} color="green" />
        {metodos.map(({ metodo, total }) => (
          <div key={metodo} className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: METODO_COLOR[metodo] ?? '#94a3b8' }} />
            <div>
              <p className="text-lg font-bold text-text-primary">{formatCurrency(total)}</p>
              <p className="text-xs text-text-secondary">{METODO_LABEL[metodo] ?? metodo}</p>
            </div>
          </div>
        ))}
      </div>
      <SectionCard title="Ingresos diarios">
        {isLoading ? <EmptyState label="Cargando..." /> : !data?.porDia.length ? <EmptyState label="Sin pagos en este período" /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.porDia} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="fecha" tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={48} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} labelFormatter={fmt} />
              <Bar dataKey="total" fill={CHART_COLORS.green} radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </SectionCard>
    </div>
  );
}

function SeccionCitas({ rango }: { rango: Rango }) {
  const { data, isLoading } = useQuery({
    queryKey: ['reporte-citas-stats', rango.fechaInicio, rango.fechaFin],
    queryFn: () => api.get('/api/reportes/citas-stats', { params: rango }).then(r => r.data as {
      total: number; porEstado: Record<string, number>; porTipo: Record<string, number>;
      tasaCancelacion: number; tasaNoAsistencia: number;
    }),
  });
  const pieData = data ? Object.entries(data.porEstado).map(([estado, value]) => ({ name: getEstadoCitaInfo(estado).label, value, color: getEstadoCitaInfo(estado).hex })) : [];
  const tipoData = data ? [{ name: 'Presencial', value: data.porTipo['presencial'] ?? 0 }, { name: 'Teleconsulta', value: data.porTipo['teleconsulta'] ?? 0 }] : [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard icon={CalendarCheck2} label="Total de citas"     value={data?.total ?? '—'}              color="primary" />
        <KpiCard icon={CalendarCheck2} label="Completadas"         value={data?.porEstado['completada'] ?? 0} color="green"   />
        <KpiCard icon={CalendarCheck2} label="Tasa cancelación"   value={`${data?.tasaCancelacion ?? 0}%`}  color="red"     />
        <KpiCard icon={CalendarCheck2} label="No asistió"         value={`${data?.tasaNoAsistencia ?? 0}%`} color="orange"  />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Distribución por estado">
          {isLoading ? <EmptyState label="Cargando..." /> : !pieData.length ? <EmptyState label="Sin citas en este período" /> : (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {pieData.map(d => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                      <span className="text-text-secondary">{d.name}</span>
                    </div>
                    <span className="font-semibold text-text-primary">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
        <SectionCard title="Presencial vs Teleconsulta">
          {isLoading ? <EmptyState label="Cargando..." /> : !data?.total ? <EmptyState label="Sin citas en este período" /> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={tipoData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" name="Citas" radius={[6, 6, 0, 0]} maxBarSize={80}>
                  <Cell fill={CHART_COLORS.primary} />
                  <Cell fill={CHART_COLORS.blue} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function SeccionProductividad({ rango }: { rango: Rango }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['reporte-productividad', rango.fechaInicio, rango.fechaFin],
    queryFn: () => api.get('/api/reportes/productividad-medicos', { params: rango }).then(r => r.data as Array<{
      medicoId: string; nombre: string; especialidad: string | null;
      total: number; completadas: number; canceladas: number; noAsistio: number; tasaCompletado: number;
    }>),
  });
  return (
    <SectionCard title="Productividad por médico">
      {isLoading ? <EmptyState label="Cargando..." /> : !data.length ? <EmptyState label="Sin citas en este período" /> : (
        <div className="space-y-3">
          <ResponsiveContainer width="100%" height={Math.max(120, data.length * 44)}>
            <BarChart layout="vertical" data={data} margin={{ top: 0, right: 80, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="nombre" width={120} tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(v: number, name: string) => [v, name === 'completadas' ? 'Completadas' : name === 'canceladas' ? 'Canceladas' : 'No asistió']} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="completadas" name="Completadas" stackId="a" fill={CHART_COLORS.green}  />
              <Bar dataKey="canceladas"  name="Canceladas"  stackId="a" fill={CHART_COLORS.red}    />
              <Bar dataKey="noAsistio"   name="No asistió"  stackId="a" fill={CHART_COLORS.orange} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="border border-gray-100 rounded-xl overflow-hidden mt-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-text-secondary">
                  <th className="text-left px-4 py-2.5 font-semibold">Médico</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Total</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Completas</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Canceladas</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Tasa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map(m => (
                  <tr key={m.medicoId} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{m.nombre}</p>
                      {m.especialidad && <p className="text-text-secondary text-[10px]">{m.especialidad}</p>}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-text-primary">{m.total}</td>
                    <td className="px-3 py-3 text-right text-green-700">{m.completadas}</td>
                    <td className="px-3 py-3 text-right text-red-600">{m.canceladas + m.noAsistio}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={cn('font-bold', m.tasaCompletado >= 80 ? 'text-green-600' : m.tasaCompletado >= 60 ? 'text-orange-600' : 'text-red-600')}>
                        {m.tasaCompletado}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function SeccionNuevosPacientes({ rango }: { rango: Rango }) {
  const { data, isLoading } = useQuery({
    queryKey: ['reporte-nuevos-pacientes', rango.fechaInicio, rango.fechaFin],
    queryFn: () => api.get('/api/reportes/nuevos-pacientes', { params: rango }).then(r => r.data as {
      totalNuevos: number; porDia: { fecha: string; total: number }[];
    }),
  });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KpiCard icon={Users} label="Nuevos pacientes en el período" value={data?.totalNuevos ?? '—'} color="blue" />
      </div>
      <SectionCard title="Altas por día">
        {isLoading ? <EmptyState label="Cargando..." /> : !data?.porDia.length ? <EmptyState label="Sin nuevos pacientes en este período" /> : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.porDia} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="fecha" tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v, 'Nuevos pacientes']} labelFormatter={fmt} />
              <Line type="monotone" dataKey="total" stroke={CHART_COLORS.blue} strokeWidth={2} dot={{ r: 4, fill: CHART_COLORS.blue }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </SectionCard>
    </div>
  );
}

function SeccionStockAlertas() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['reporte-stock-alertas'],
    queryFn: () => api.get('/api/reportes/stock-alertas').then(r => r.data as Array<{
      id: string; nombre: string; stock: number; stockMinimo: number; deficit: number; precio: number;
    }>),
    staleTime: 60_000,
  });
  return (
    <SectionCard title="Alertas de inventario">
      {isLoading ? <EmptyState label="Cargando..." /> : !data.length ? (
        <div className="flex flex-col items-center justify-center h-32 text-sm text-green-600">
          <span className="text-2xl mb-1">✓</span>
          Todo el inventario está sobre el mínimo
        </div>
      ) : (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-text-secondary">
                <th className="text-left px-4 py-2.5 font-semibold">Producto</th>
                <th className="text-right px-3 py-2.5 font-semibold">Stock actual</th>
                <th className="text-right px-3 py-2.5 font-semibold">Mínimo</th>
                <th className="text-right px-4 py-2.5 font-semibold">Déficit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.map(p => {
                const agotado = p.stock === 0;
                return (
                  <tr key={p.id} className={cn('hover:bg-gray-50/50', agotado && 'bg-red-50/30')}>
                    <td className="px-4 py-3 flex items-center gap-2">
                      <AlertTriangle size={13} className={agotado ? 'text-red-500 flex-shrink-0' : 'text-orange-400 flex-shrink-0'} />
                      <span className="font-medium text-text-primary">{p.nombre}</span>
                    </td>
                    <td className={cn('px-3 py-3 text-right font-bold', agotado ? 'text-red-600' : 'text-orange-600')}>{p.stock}</td>
                    <td className="px-3 py-3 text-right text-text-secondary">{p.stockMinimo}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-600">−{p.deficit}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/* ── Médico sections ────────────────────────────────────────────────────── */

function SeccionMisConsultas({ rango, medicoId }: { rango: Rango; medicoId?: string }) {
  const params = { ...rango, ...(medicoId ? { medicoId } : {}) };
  const { data, isLoading } = useQuery({
    queryKey: ['reporte-mis-consultas', params],
    queryFn: () => api.get('/api/reportes/medico/mis-consultas', { params }).then(r => r.data as {
      total: number; completadas: number; canceladas: number; noAsistio: number;
      porEstado: Record<string, number>;
      porDia: { fecha: string; total: number; completadas: number }[];
      duracionPromedio: number | null;
    }),
  });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard icon={CalendarCheck2} label="Citas en el período"   value={data?.total ?? '—'}        color="primary" />
        <KpiCard icon={UserCheck}      label="Completadas"            value={data?.completadas ?? '—'}  color="green"   />
        <KpiCard icon={CalendarCheck2} label="Canceladas"            value={data?.canceladas ?? '—'}   color="red"     />
        <KpiCard icon={Clock}          label="Duración promedio"
          value={data?.duracionPromedio != null ? `${data.duracionPromedio} min` : '—'}
          sub={data?.duracionPromedio == null ? 'Sin datos de tiempo' : undefined}
          color="blue"
        />
      </div>
      <SectionCard title="Citas por día">
        {isLoading ? <EmptyState label="Cargando..." /> : !data?.porDia.length ? <EmptyState label="Sin citas en este período" /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.porDia} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="fecha" tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={fmt} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="completadas" name="Completadas" stackId="a" fill={CHART_COLORS.green}  radius={[0, 0, 0, 0]} />
              <Bar dataKey="total"       name="Resto"       stackId="a" fill="#e2e8f0"             radius={[4, 4, 0, 0]}
                label={false}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </SectionCard>
    </div>
  );
}

function SeccionDiagnosticos({ rango, medicoId }: { rango: Rango; medicoId?: string }) {
  const params = { ...rango, ...(medicoId ? { medicoId } : {}) };
  const { data = [], isLoading } = useQuery({
    queryKey: ['reporte-diagnosticos', params],
    queryFn: () => api.get('/api/reportes/medico/diagnosticos-frecuentes', { params }).then(r => r.data as Array<{
      codigoCie11: string; tituloCie11: string; total: number;
    }>),
  });

  const max = data[0]?.total ?? 1;

  return (
    <SectionCard title="Diagnósticos más frecuentes (CIE-11)">
      {isLoading ? <EmptyState label="Cargando..." /> : !data.length ? <EmptyState label="Sin diagnósticos CIE-11 registrados en este período" /> : (
        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={d.codigoCie11 ?? i} className="flex items-center gap-3">
              <div className="w-12 text-right flex-shrink-0">
                <span className="text-xs font-bold text-text-secondary tabular-nums">{d.codigoCie11}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-xs font-medium text-text-primary truncate pr-2">{d.tituloCie11}</p>
                  <span className="text-xs font-bold text-primary-600 flex-shrink-0">{d.total}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-400 transition-all"
                    style={{ width: `${(d.total / max) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function SeccionSeguimiento({ medicoId, meses, setMeses }: {
  medicoId?: string; meses: number; setMeses: (m: number) => void;
}) {
  const params: Record<string, string | number> = { meses, ...(medicoId ? { medicoId } : {}) };
  const { data = [], isLoading } = useQuery({
    queryKey: ['reporte-seguimiento', medicoId, meses],
    queryFn: () => api.get('/api/reportes/medico/pacientes-sin-consulta', { params }).then(r => r.data as Array<{
      pacienteId: string; nombre: string; apellidoPaterno: string; apellidoMaterno: string | null;
      telefono: string | null; ultimaCita: string; mesesAusencia: number;
    }>),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <KpiCard icon={Users} label={`Pacientes sin cita hace más de ${meses} meses`} value={data.length} color="orange" />
        <div className="relative ml-auto">
          <select
            value={meses}
            onChange={e => setMeses(Number(e.target.value))}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
          >
            {[1, 2, 3, 6, 12].map(m => (
              <option key={m} value={m}>Más de {m} {m === 1 ? 'mes' : 'meses'}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        </div>
      </div>

      <SectionCard title="Pacientes que no han regresado">
        {isLoading ? <EmptyState label="Cargando..." /> : !data.length ? (
          <div className="flex flex-col items-center justify-center h-32 text-sm text-green-600">
            <span className="text-2xl mb-1">✓</span>
            Todos los pacientes tienen citas recientes
          </div>
        ) : (
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-text-secondary">
                  <th className="text-left px-4 py-2.5 font-semibold">Paciente</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Teléfono</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Última cita</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Ausencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map(p => (
                  <tr key={p.pacienteId} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {p.nombre} {p.apellidoPaterno} {p.apellidoMaterno ?? ''}
                    </td>
                    <td className="px-3 py-3 text-text-secondary">{p.telefono ?? '—'}</td>
                    <td className="px-3 py-3 text-right text-text-secondary">
                      {format(new Date(p.ultimaCita), 'd MMM yyyy', { locale: es })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={cn(
                        'font-bold',
                        p.mesesAusencia >= 6 ? 'text-red-600' : p.mesesAusencia >= 3 ? 'text-orange-600' : 'text-text-secondary',
                      )}>
                        {p.mesesAusencia} m
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ── Tab configs ────────────────────────────────────────────────────────── */

type AdminTab  = 'ingresos' | 'citas' | 'productividad' | 'pacientes' | 'stock';
type MedicoTab = 'mis-consultas' | 'diagnosticos' | 'seguimiento';

const ADMIN_TABS: { id: AdminTab; label: string; icon: React.ElementType }[] = [
  { id: 'ingresos',      label: 'Ingresos',     icon: TrendingUp     },
  { id: 'citas',         label: 'Citas',         icon: CalendarCheck2 },
  { id: 'productividad', label: 'Productividad', icon: Stethoscope    },
  { id: 'pacientes',     label: 'Pacientes',     icon: Users          },
  { id: 'stock',         label: 'Inventario',    icon: Package        },
];

const MEDICO_TABS: { id: MedicoTab; label: string; icon: React.ElementType }[] = [
  { id: 'mis-consultas', label: 'Mis consultas',  icon: CalendarCheck2 },
  { id: 'diagnosticos',  label: 'Diagnósticos',   icon: ClipboardList  },
  { id: 'seguimiento',   label: 'Seguimiento',    icon: UserCheck      },
];

/* ── TabBar ─────────────────────────────────────────────────────────────── */

function TabBar<T extends string>({
  tabs, active, onChange,
}: { tabs: { id: T; label: string; icon: React.ElementType }[]; active: T; onChange: (t: T) => void }) {
  return (
    <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0',
            active === t.id ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
          )}
        >
          <t.icon size={13} />
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────────────────────── */

export function ReportesPage() {
  const { usuario } = useAuthStore();
  const rolId = usuario?.rolId;

  const isAdmin    = rolId === ROL.SUPER_ADMIN || rolId === ROL.ADMIN_CLINICA;
  const isMedico   = rolId === ROL.MEDICO || rolId === ROL.MEDICO_ADMIN;
  const isAsistente = rolId === ROL.ASISTENTE;
  const showMedicoSection = isMedico || isAsistente || isAdmin;

  // Date range state (shared between both sections)
  const [preset, setPreset] = useState<Preset>('mes_actual');
  const [custom, setCustom] = useState<Rango>({
    fechaInicio: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    fechaFin:    format(new Date(), 'yyyy-MM-dd'),
  });
  const rango = useMemo(() => presetToRango(preset, custom), [preset, custom]);

  // Admin tabs
  const [adminTab, setAdminTab]   = useState<AdminTab>('ingresos');
  // Médico tabs
  const [medicoTab, setMedicoTab] = useState<MedicoTab>('mis-consultas');
  // Médico selector (for admin/asistente viewing médico reports)
  const [selectedMedicoId, setSelectedMedicoId] = useState<string>('');
  // Meses para seguimiento
  const [meses, setMeses] = useState(3);

  // Active section (which group of tabs is showing)
  const [section, setSection] = useState<'clinica' | 'medico'>(isAdmin ? 'clinica' : 'medico');

  // Médicos list (for admin/asistente selector)
  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data as Array<{ id: string; nombre: string }>),
    enabled: isAdmin || isAsistente,
    staleTime: 60_000,
  });

  // For MEDICO: no selector, medicoId comes from JWT (backend resolves it)
  const medicoIdParam = isMedico ? undefined : (selectedMedicoId || undefined);

  const presetLabel = PRESETS.find(p => p.value === preset)?.label ?? '';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Reportes</h2>
          <p className="text-sm text-text-secondary mt-0.5">Estadísticas y análisis</p>
        </div>

        {/* Date range selector */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <select
              value={preset}
              onChange={e => setPreset(e.target.value as Preset)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
          </div>
          {preset === 'personalizado' && (
            <>
              <input type="date" value={custom.fechaInicio} max={custom.fechaFin}
                onChange={e => setCustom(s => ({ ...s, fechaInicio: e.target.value }))}
                className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <span className="text-text-secondary text-sm">—</span>
              <input type="date" value={custom.fechaFin} min={custom.fechaInicio} max={format(new Date(), 'yyyy-MM-dd')}
                onChange={e => setCustom(s => ({ ...s, fechaFin: e.target.value }))}
                className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </>
          )}
        </div>
      </div>

      <p className="text-xs text-text-secondary -mt-2">
        {presetLabel} · {format(new Date(rango.fechaInicio + 'T12:00:00'), 'd MMM yyyy', { locale: es })} al{' '}
        {format(new Date(rango.fechaFin + 'T12:00:00'), 'd MMM yyyy', { locale: es })}
      </p>

      {/* Section switcher — solo si el usuario tiene acceso a ambas secciones */}
      {isAdmin && (
        <div className="flex gap-2">
          <button
            onClick={() => setSection('clinica')}
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
              section === 'clinica'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-text-secondary border-gray-200 hover:border-gray-300',
            )}
          >
            Clínica
          </button>
          <button
            onClick={() => setSection('medico')}
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
              section === 'medico'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-text-secondary border-gray-200 hover:border-gray-300',
            )}
          >
            Médico
          </button>
        </div>
      )}

      {/* ── Admin section ── */}
      {isAdmin && section === 'clinica' && (
        <div className="space-y-5">
          <TabBar tabs={ADMIN_TABS} active={adminTab} onChange={setAdminTab} />
          {adminTab === 'ingresos'      && <SeccionIngresos       rango={rango} />}
          {adminTab === 'citas'         && <SeccionCitas           rango={rango} />}
          {adminTab === 'productividad' && <SeccionProductividad   rango={rango} />}
          {adminTab === 'pacientes'     && <SeccionNuevosPacientes  rango={rango} />}
          {adminTab === 'stock'         && <SeccionStockAlertas />}
        </div>
      )}

      {/* ── Médico section ── */}
      {showMedicoSection && (isAdmin ? section === 'medico' : true) && (
        <div className="space-y-5">
          {/* Doctor selector para admin/asistente */}
          {(isAdmin || isAsistente) && medicos.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-text-secondary whitespace-nowrap">Ver datos de:</label>
              <div className="relative">
                <select
                  value={selectedMedicoId}
                  onChange={e => setSelectedMedicoId(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="">Seleccionar médico</option>
                  {medicos.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
              </div>
            </div>
          )}

          {/* Si admin no ha seleccionado médico, mostrar aviso */}
          {(isAdmin || isAsistente) && !selectedMedicoId ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-100">
              <Stethoscope size={36} className="text-gray-200 mb-3" />
              <p className="text-sm font-medium text-text-primary">Selecciona un médico</p>
              <p className="text-xs text-text-secondary mt-1">Elige un médico del selector para ver sus reportes</p>
            </div>
          ) : (
            <>
              <TabBar tabs={MEDICO_TABS} active={medicoTab} onChange={setMedicoTab} />
              {medicoTab === 'mis-consultas' && <SeccionMisConsultas rango={rango} medicoId={medicoIdParam} />}
              {medicoTab === 'diagnosticos'  && <SeccionDiagnosticos  rango={rango} medicoId={medicoIdParam} />}
              {medicoTab === 'seguimiento'   && <SeccionSeguimiento   medicoId={medicoIdParam} meses={meses} setMeses={setMeses} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}
